import React, { useMemo, useState } from 'react';
import { useGetProcurements } from '@/hooks/use-procurement';
import { format, parseISO, isAfter, isBefore, startOfMonth, endOfMonth, subMonths, startOfWeek, startOfQuarter, endOfWeek, endOfQuarter, startOfDay } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Calendar as CalendarIcon, Users, Package, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Procurement } from '@/types/database.types';
import { calculateTotalAmount } from '@/utils/procurementCalculations';
import * as XLSX from 'xlsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

const FinanceReports = () => {
  const { data: procurements, isLoading } = useGetProcurements();
  const [timeRange, setTimeRange] = useState('3months');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [aggregationPeriod, setAggregationPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [exportStatusFilter, setExportStatusFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all');
  
  const filteredProcurements = useMemo(() => {
    if (!procurements) return [];
    
    const now = new Date();
    let filterStartDate: Date;
    let filterEndDate: Date;
    
    if (timeRange === 'custom' && startDate && endDate) {
      filterStartDate = startDate;
      filterEndDate = endDate;
    } else {
      switch (timeRange) {
        case '1month':
          filterStartDate = startOfMonth(subMonths(now, 0));
          break;
        case '3months':
          filterStartDate = startOfMonth(subMonths(now, 2));
          break;
        case '6months':
          filterStartDate = startOfMonth(subMonths(now, 5));
          break;
        case '1year':
          filterStartDate = startOfMonth(subMonths(now, 11));
          break;
        default:
          filterStartDate = startOfMonth(subMonths(now, 2));
      }
      filterEndDate = endOfMonth(now);
    }
    
    return (procurements as Procurement[]).filter(p => {
      const procurementDate = parseISO(p.created_at);
      return isAfter(procurementDate, filterStartDate) && isBefore(procurementDate, filterEndDate);
    });
  }, [procurements, timeRange, startDate, endDate]);

  const exportFilteredProcurements = useMemo(() => {
    switch (exportStatusFilter) {
      case 'approved':
        return filteredProcurements.filter(p => 
          ['price_confirmed', 'quality_confirmed', 'agreement_signed', 
           'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed']
          .includes(p.status)
        );
      case 'pending':
        return filteredProcurements.filter(p => 
          ['pending_finance', 'pending', 'initiated', 'awaiting_approval']
          .includes(p.status)
        );
      case 'rejected':
        return filteredProcurements.filter(p => p.status === 'rejected');
      case 'all':
      default:
        return filteredProcurements;
    }
  }, [filteredProcurements, exportStatusFilter]);

  const exportAggregatedData = useMemo(() => {
    const dataMap: Record<string, { period: string; total: number; count: number; approved: number; rejected: number; pending: number; quantity: number }> = {};
    
    exportFilteredProcurements.forEach(p => {
      const procurementDate = parseISO(p.created_at);
      let periodKey: string;
      let periodLabel: string;
      
      switch (aggregationPeriod) {
        case 'daily':
          periodKey = format(procurementDate, 'yyyy-MM-dd');
          periodLabel = format(procurementDate, 'MMM dd, yyyy');
          break;
        case 'weekly':
          const weekStart = startOfWeek(procurementDate);
          periodKey = format(weekStart, 'yyyy-MM-dd');
          periodLabel = `Week of ${format(weekStart, 'MMM dd, yyyy')}`;
          break;
        case 'monthly':
          periodKey = format(procurementDate, 'yyyy-MM');
          periodLabel = format(procurementDate, 'MMM yyyy');
          break;
        case 'quarterly':
          const quarter = Math.floor(procurementDate.getMonth() / 3) + 1;
          periodKey = `${procurementDate.getFullYear()}-Q${quarter}`;
          periodLabel = `Q${quarter} ${procurementDate.getFullYear()}`;
          break;
        default:
          periodKey = format(procurementDate, 'yyyy-MM');
          periodLabel = format(procurementDate, 'MMM yyyy');
      }
      
      if (!dataMap[periodKey]) {
        dataMap[periodKey] = { period: periodLabel, total: 0, count: 0, approved: 0, rejected: 0, pending: 0, quantity: 0 };
      }
      
      dataMap[periodKey].total += calculateTotalAmount(p);
      dataMap[periodKey].count += 1;
      dataMap[periodKey].quantity += p.total_quantity || 0;
      
      if (['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)) {
        dataMap[periodKey].approved += 1;
      } else if (p.status === 'rejected') {
        dataMap[periodKey].rejected += 1;
      } else {
        dataMap[periodKey].pending += 1;
      }
    });
    
    return Object.values(dataMap).sort((a, b) => {
      const dateA = new Date(a.period);
      const dateB = new Date(b.period);
      return dateA.getTime() - dateB.getTime();
    });
  }, [exportFilteredProcurements, aggregationPeriod]);

  const aggregatedData = useMemo(() => {
    const dataMap: Record<string, { period: string; total: number; count: number; approved: number; rejected: number; pending: number; quantity: number }> = {};
    
    filteredProcurements.forEach(p => {
      const procurementDate = parseISO(p.created_at);
      let periodKey: string;
      let periodLabel: string;
      
      switch (aggregationPeriod) {
        case 'daily':
          periodKey = format(procurementDate, 'yyyy-MM-dd');
          periodLabel = format(procurementDate, 'MMM dd, yyyy');
          break;
        case 'weekly':
          const weekStart = startOfWeek(procurementDate);
          periodKey = format(weekStart, 'yyyy-MM-dd');
          periodLabel = `Week of ${format(weekStart, 'MMM dd, yyyy')}`;
          break;
        case 'monthly':
          periodKey = format(procurementDate, 'yyyy-MM');
          periodLabel = format(procurementDate, 'MMM yyyy');
          break;
        case 'quarterly':
          const quarter = Math.floor(procurementDate.getMonth() / 3) + 1;
          periodKey = `${procurementDate.getFullYear()}-Q${quarter}`;
          periodLabel = `Q${quarter} ${procurementDate.getFullYear()}`;
          break;
        default:
          periodKey = format(procurementDate, 'yyyy-MM');
          periodLabel = format(procurementDate, 'MMM yyyy');
      }
      
      if (!dataMap[periodKey]) {
        dataMap[periodKey] = { period: periodLabel, total: 0, count: 0, approved: 0, rejected: 0, pending: 0, quantity: 0 };
      }
      
      dataMap[periodKey].total += calculateTotalAmount(p);
      dataMap[periodKey].count += 1;
      dataMap[periodKey].quantity += p.total_quantity || 0;
      
      if (['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)) {
        dataMap[periodKey].approved += 1;
      } else if (p.status === 'rejected') {
        dataMap[periodKey].rejected += 1;
      } else {
        dataMap[periodKey].pending += 1;
      }
    });
    
    return Object.values(dataMap).sort((a, b) => {
      const dateA = new Date(a.period);
      const dateB = new Date(b.period);
      return dateA.getTime() - dateB.getTime();
    });
  }, [filteredProcurements, aggregationPeriod]);

  // Keep monthlyData for backward compatibility with charts
  const monthlyData = useMemo(() => {
    const monthMap: Record<string, { month: string; total: number; count: number; approved: number; rejected: number; pending: number }> = {};
    
    filteredProcurements.forEach(p => {
      const month = format(parseISO(p.created_at), 'MMM yyyy');
      if (!monthMap[month]) {
        monthMap[month] = { month, total: 0, count: 0, approved: 0, rejected: 0, pending: 0 };
      }
      
      monthMap[month].total += calculateTotalAmount(p);
      monthMap[month].count += 1;
      
      if (['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)) {
        monthMap[month].approved += 1;
      } else if (p.status === 'rejected') {
        monthMap[month].rejected += 1;
      } else {
        monthMap[month].pending += 1;
      }
    });
    
    return Object.values(monthMap).sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateA.getTime() - dateB.getTime();
    });
  }, [filteredProcurements]);

  const exportToCSV = () => {
    if (!exportFilteredProcurements.length) return;
    
    const baseHeaders = ['Period', 'Total Transactions', 'Total Amount (Br)', 'Total Quantity (kg)'];
    const statusHeaders = exportStatusFilter === 'all' 
      ? ['Approved', 'Rejected', 'Pending']
      : [exportStatusFilter.charAt(0).toUpperCase() + exportStatusFilter.slice(1)];
    const headers = [...baseHeaders, ...statusHeaders];
    const csvRows = [headers];
    
    exportAggregatedData.forEach(data => {
      const baseData = [
        data.period,
        data.count.toString(),
        data.total.toFixed(2),
        data.quantity.toFixed(2)
      ];
      const statusData = exportStatusFilter === 'all'
        ? [data.approved.toString(), data.rejected.toString(), data.pending.toString()]
        : [data.count.toString()];
      csvRows.push([...baseData, ...statusData]);
    });
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `finance_report_${exportStatusFilter}_${aggregationPeriod}_${timeRange}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    if (!exportFilteredProcurements.length) return;

    const wb = XLSX.utils.book_new();

    const statusLabel = exportStatusFilter === 'all' ? 'All Statuses' : `${exportStatusFilter.charAt(0).toUpperCase() + exportStatusFilter.slice(1)} Only`;

    // Summary sheet
    const summaryData = [
      [`Finance Report - ${aggregationPeriod.charAt(0).toUpperCase() + aggregationPeriod.slice(1)} - ${timeRange}`],
      ['Generated on:', format(new Date(), 'yyyy-MM-dd HH:mm:ss')],
      ['Status Filter:', statusLabel],
      ['Time Range:', timeRange],
      ['Aggregation Period:', aggregationPeriod.charAt(0).toUpperCase() + aggregationPeriod.slice(1)],
      ['Total Transactions:', exportFilteredProcurements.length],
      ['Total Amount:', `Br ${exportFilteredProcurements.reduce((sum, p) => sum + calculateTotalAmount(p), 0).toFixed(2)}`],
      ['Total Quantity:', `${exportFilteredProcurements.reduce((sum, p) => sum + (p.total_quantity || 0), 0).toFixed(2)} kg`],
      [],
      [`${aggregationPeriod.charAt(0).toUpperCase() + aggregationPeriod.slice(1)} Breakdown:`],
    ];

    const baseHeaders = ['Period', 'Total Transactions', 'Total Amount (Br)', 'Total Quantity (kg)'];
    const statusHeaders = exportStatusFilter === 'all' 
      ? ['Approved', 'Rejected', 'Pending']
      : [exportStatusFilter.charAt(0).toUpperCase() + exportStatusFilter.slice(1)];
    summaryData.push([...baseHeaders, ...statusHeaders]);

    exportAggregatedData.forEach(data => {
      const baseData = [data.period, data.count, data.total.toFixed(2), data.quantity.toFixed(2)];
      const statusData = exportStatusFilter === 'all'
        ? [data.approved, data.rejected, data.pending]
        : [data.count];
      summaryData.push([...baseData, ...statusData]);
    });

    const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');

    // Detailed transactions
    const detailedHeaders = [
      'ID', 'Product', 'Source Type', 'Created Date', 'Status',
      'Payment Term', 'Quantity', 'Price Per Unit', 'Transport Cost',
      'Loading Cost', 'Agent Cost', 'Other Cost', 'Total Amount'
    ];
    
    const detailedData = [detailedHeaders];
    
    exportFilteredProcurements.forEach(procurement => {
      detailedData.push([
        procurement.id,
        procurement.product_name || 'N/A',
        procurement.source_type,
        format(parseISO(procurement.created_at), 'yyyy-MM-dd'),
        procurement.status,
        procurement.finance?.payment_term_type || 'N/A',
        procurement.total_quantity?.toString() || '0',
        procurement.costs?.price_per_unit?.toString() || '0',
        procurement.costs?.transport_cost_per_unit?.toString() || '0',
        procurement.costs?.loading_unloading_cost_per_unit?.toString() || '0',
        procurement.costs?.agent_cost_per_unit?.toString() || '0',
        procurement.costs?.other_cost_per_unit?.toString() || '0',
        calculateTotalAmount(procurement).toString()
      ]);
    });

    const detailedWS = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, detailedWS, 'Detailed Data');

    XLSX.writeFile(wb, `finance_report_${exportStatusFilter}_${aggregationPeriod}_${timeRange}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Finance Reports</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1month">Last Month</SelectItem>
              <SelectItem value="3months">Last 3 Months</SelectItem>
              <SelectItem value="6months">Last 6 Months</SelectItem>
              <SelectItem value="1year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {timeRange === 'custom' && (
            <div className="flex gap-2 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <span>to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {endDate ? format(endDate, 'MMM dd, yyyy') : 'End Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          <Select value={aggregationPeriod} onValueChange={(value: any) => setAggregationPeriod(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Aggregation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
            </SelectContent>
          </Select>

          <Select value={exportStatusFilter} onValueChange={(value: any) => setExportStatusFilter(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Export Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="approved">Approved Only</SelectItem>
              <SelectItem value="pending">Pending Only</SelectItem>
              <SelectItem value="rejected">Rejected Only</SelectItem>
            </SelectContent>
          </Select>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportToCSV}>
                <FileText className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {filteredProcurements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Package className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No data available</h3>
          <p className="text-muted-foreground mt-2">
            No procurement data found for the selected time range.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <DollarSign className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <h3 className="text-2xl font-bold">
                      Br {filteredProcurements.reduce((sum, p) => sum + calculateTotalAmount(p), 0).toFixed(2)}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Package className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Transactions</p>
                    <h3 className="text-2xl font-bold">{filteredProcurements.length}</h3>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <TrendingUp className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Transaction</p>
                    <h3 className="text-2xl font-bold">
                      Br {filteredProcurements.length > 0 
                        ? (filteredProcurements.reduce((sum, p) => sum + calculateTotalAmount(p), 0) / filteredProcurements.length).toFixed(2)
                        : "0.00"}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <CalendarIcon className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Reporting Period</p>
                    <h3 className="text-lg font-bold capitalize">{timeRange}</h3>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-indigo-100 rounded-full">
                    <Package className="h-6 w-6 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Quantity</p>
                    <h3 className="text-2xl font-bold">
                      {filteredProcurements.reduce((sum, p) => sum + (p.total_quantity || 0), 0).toLocaleString()} kg
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <Tabs defaultValue="monthly">
            <TabsList className="mb-4">
              <TabsTrigger value="monthly">Monthly Trends</TabsTrigger>
              <TabsTrigger value="status">Status Breakdown</TabsTrigger>
              <TabsTrigger value="source">Source Analysis</TabsTrigger>
            </TabsList>
            
            <TabsContent value="monthly">
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Performance</CardTitle>
                  <CardDescription>Transaction volume and value over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" orientation="left" tickFormatter={(value) => `Br ${value}`} />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip 
                          formatter={(value, name) => {
                            if (name === 'total') return [`Br ${Number(value).toFixed(2)}`, 'Total Value'];
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="total" fill="#8884d8" name="Total Value" />
                        <Bar yAxisId="right" dataKey="count" fill="#82ca9d" name="Transaction Count" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="status">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Transaction Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Approved', value: filteredProcurements.filter(p => ['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)).length },
                              { name: 'Pending', value: filteredProcurements.filter(p => ['pending_finance', 'pending', 'initiated', 'awaiting_approval'].includes(p.status)).length },
                              { name: 'Rejected', value: filteredProcurements.filter(p => p.status === 'rejected').length }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {[
                              { name: 'Approved', value: filteredProcurements.filter(p => ['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)).length },
                              { name: 'Pending', value: filteredProcurements.filter(p => ['pending_finance', 'pending', 'initiated', 'awaiting_approval'].includes(p.status)).length },
                              { name: 'Rejected', value: filteredProcurements.filter(p => p.status === 'rejected').length }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Status Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="approved" stroke="#82ca9d" name="Approved" />
                          <Line type="monotone" dataKey="pending" stroke="#8884d8" name="Pending" />
                          <Line type="monotone" dataKey="rejected" stroke="#ff7300" name="Rejected" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="source">
              <Card>
                <CardHeader>
                  <CardTitle>Source Type Analysis</CardTitle>
                  <CardDescription>Transaction distribution by source type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.entries(
                        filteredProcurements.reduce((acc, p) => {
                          acc[p.source_type] = (acc[p.source_type] || 0) + calculateTotalAmount(p);
                          return acc;
                        }, {} as Record<string, number>)
                      ).map(([source, amount]) => ({ source, amount }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="source" />
                        <YAxis tickFormatter={(value) => `Br ${value}`} />
                        <Tooltip formatter={(value) => [`Br ${Number(value).toFixed(2)}`, 'Total Value']} />
                        <Legend />
                        <Bar dataKey="amount" fill="#8884d8" name="Total Value" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default FinanceReports;
